import React, { useEffect, useState } from 'react';
import PageLayout from '../../layouts/PageLayout';
import HeroSection from 'components/HeroSection';
import Brochure from 'components/Brochure';
import TextPage from 'components/TextPage';
import ShowRooms from 'components/ShowRooms';
import { getB2BServicesData, getHeroSectionData } from '../../api-sanity/api';
import { useRouteIsLoading } from 'context/RouteIsLoadingContext';
import { useTheme } from 'hooks/useTheme';
import Client from 'components/Client';
import PresentationInNr from 'components/PresentationInNr';
import usePageLoadProgress from 'hooks/usePageLoadProgress';
import { useSanityMetaTags } from 'hooks/useSanityMetaTags';

const pID = {
  P_1: '81ac97e5-cfe9-46eb-bd6d-d9f02f162a70',
};

const b2bServicesIds = {
  heroId: 'be267bef-75cb-42c1-a8b3-7ae4078f9c6d',
  brochureId: 'cd9307de-2cef-43b3-aa4a-84be7fa939d6',
  showRoomsId: '09bd3446-aee0-48b8-85f9-4674eae2c18b',
};

const B2BServices = () => {
  const [b2bServicesData, setB2bServicesData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [heroData, setHeroData] = useState(null);
  const [isHeroLoading, setIsHeroLoading] = useState(true);
  const { setIsLoading: setRouteContextIsLoading } = useRouteIsLoading();
  const { isDarkMode } = useTheme();

  // Fetch and apply meta tags from Sanity
  useSanityMetaTags('b2b');

  const { loadingProgress, isDataReady, apiDataResults } = usePageLoadProgress(
    (onProgress) => getB2BServicesData(b2bServicesIds, onProgress),
  );

  useEffect(() => {
    if (isDataReady && apiDataResults) {
      const [data] = apiDataResults;
      setB2bServicesData(data);
      setIsLoading(false);
    }
  }, [isDataReady, apiDataResults]);

  // Fetched separately from the rest of the page's data so the hero can
  // render as soon as this small, fast request resolves.
  useEffect(() => {
    let cancelled = false;
    getHeroSectionData(b2bServicesIds.heroId).then((data) => {
      if (!cancelled) {
        setHeroData(data);
        setIsHeroLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setRouteContextIsLoading(isLoading);
  }, [isLoading, setRouteContextIsLoading]);

  const getParagraphById = (id) => {
    if (!b2bServicesData?.textPages) return null;
    return b2bServicesData.textPages.find((p) => p._id === id);
  };

  return (
    <PageLayout>
      <HeroSection
        imageUrl={
          isDarkMode
            ? heroData?.darkModeImageUrl || heroData?.lightModeImageUrl
            : heroData?.lightModeImageUrl
        }
        title={heroData?.title}
        titleHighlight={heroData?.titleHighlight}
        subtitle={heroData?.subtitle}
        description={heroData?.description}
        buttonText={heroData?.buttonText}
        buttonLink={heroData?.buttonLink}
        showThemeToggle={heroData?.showThemeToggle}
        loaderProgress={loadingProgress}
        isLoading={isHeroLoading}
      />
      {!isLoading && getParagraphById(pID.P_1) && (
        <TextPage
          key={getParagraphById(pID.P_1)?._id}
          type={getParagraphById(pID.P_1)?.type}
          titleActive={getParagraphById(pID.P_1)?.titleActive}
          titleInactive={getParagraphById(pID.P_1)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_1)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_1)?.paragraphText}
          buttonText={getParagraphById(pID.P_1)?.buttonText}
          buttonLink={getParagraphById(pID.P_1)?.buttonLink}
        />
      )}
      {!isLoading && b2bServicesData?.showRoomsData && (
        <ShowRooms
          showRoomsData={b2bServicesData.showRoomsData}
          scaleLastImage={true}
        />
      )}
      {!isLoading && <Client clientData={b2bServicesData?.clientData} />}
      {!isLoading && (
        <PresentationInNr data={b2bServicesData?.presentationInNumbers} />
      )}
      {!isLoading && <Brochure data={b2bServicesData?.brochureData} />}
    </PageLayout>
  );
};

export default B2BServices;
